<template>
  <div v-if="showHelp" class="notification is-info is-size-7 mb-3">
    <button class="delete" @click="showHelp = false" />
    <span class="icon"><i class="fa-solid fa-info-circle" /></span>
    <section>
      <p class="title">Who can see my items?</p>
      <ul>
        <li>
          <code class="has-text-primary">Public access</code>
          When items are
          <b>public</b>
          , everyone can
          <b>view</b>
          them. To set/revoke the public access on the selected, use the
          "set/revoke public access" buttons.
        </li>
        <li>
          <code class="has-text-primary">Fine tune access rights</code>
          Explicit permissions can be set to let users
          <b>view</b>
          ,
          <b>edit</b>
          or
          <b>delete</b>
          your items. Access rights can be fine-tuned at the user or group
          level. When setting permissions for a group, it applies for all
          members of this group (regardless of when they joined). To grant new
          permissions or revoke existing ones, click the "fine tune access
          rights" button and fill up the form.
        </li>
      </ul>
    </section>
  </div>

  <div class="form-fields spacious">
    <div class="field editing">
      <ItemDetailField label="Access" :is-editing="true">
        <div
          class="is-flex is-align-items-center is-flex-wrap-wrap"
          style="gap: var(--space-2)"
        >
          <button
            class="button is-small"
            :class="{ 'is-primary is-selected': fineTuneAccess }"
            @click="toggleFineTune"
          >
            Fine Tune Access Rights
          </button>
          <span class="has-text-weight-bold">and / or</span>
          <div class="buttons has-addons mb-0">
            <button
              class="button is-small"
              :class="{ 'is-primary is-selected': publicAccess === true }"
              @click="togglePublicAccess(true)"
            >
              Set public access
            </button>
            <button
              class="button is-small"
              :class="{ 'is-primary is-selected': publicAccess === false }"
              @click="togglePublicAccess(false)"
            >
              Revoke public access
            </button>
          </div>
          <a v-if="!showHelp" class="is-size-7" @click="showHelp = true">
            <span class="icon"><i class="fa-solid fa-info-circle" /></span>
            Help
          </a>
        </div>
      </ItemDetailField>
    </div>

    <div v-if="fineTuneAccess" class="field editing">
      <ItemDetailField label="Operation" :is-editing="true">
        <div class="buttons has-addons mb-0">
          <button
            v-for="op in OPERATIONS"
            :key="op"
            class="button is-small"
            :class="{ 'is-primary is-selected': chosenOperation === op }"
            @click="chosenOperation = op"
          >
            {{ capitalize(`${op} access`) }}
          </button>
        </div>
      </ItemDetailField>
    </div>

    <div v-if="fineTuneAccess" class="field editing">
      <ItemDetailField label="Value" :is-editing="true">
        <BatchEditPermissionsWidget
          :permissions-field="permissionsField"
          @permissions-change="(p) => (permissions = p)"
          @users-change="(u) => (users = u)"
          @groups-change="(g) => (groups = g)"
        />
      </ItemDetailField>
    </div>
  </div>

  <div
    v-if="hasTunedAccess || publicAccess !== null"
    class="notification is-info is-size-7 mt-3"
  >
    <section>
      <p class="title">Upon confirming:</p>
      <ul>
        <li v-if="publicAccess !== null">
          Public access is going to be
          <span
            v-if="publicAccess === true"
            class="has-text-success has-text-weight-bold"
          >
            granted
          </span>
          <span
            v-else-if="publicAccess === false"
            class="has-text-danger has-text-weight-bold"
          >
            revoked
          </span>
          for selected items
        </li>
        <li v-if="hasTunedAccess">
          Access to items
          <span v-if="permissions.length > 0">
            (
            <b>{{ permissionsLabels.join(', ') }}</b>
            )
          </span>
          is going to be
          <span v-if="chosenOperation === 'grant'">
            <b class="has-text-success">granted</b>
            to
          </span>
          <span v-else-if="chosenOperation === 'revoke'">
            <b class="has-text-danger">revoked</b>
            for
          </span>
          <span v-if="usernames.length > 0">
            {{ pluralize('user', usernames.length) }}
            (
            <b>{{ usernames.join(', ') }}</b>
            )
          </span>
          <span v-if="usernames.length > 0 && groupNames.length > 0">and</span>
          <span v-if="groupNames.length > 0">
            {{ pluralize('group', groupNames.length) }}
            (
            <b>{{ groupNames.join(', ') }}</b>
            )
          </span>
        </li>
      </ul>
    </section>
    <section v-if="chosenOperation === 'revoke' && usernames.length > 0">
      <p class="title">
        <span class="icon is-small has-text-warning-light">
          <i class="fa fa-exclamation-triangle fa-xs" />
        </span>
        Note
      </p>
      <p>
        {{ capitalize(pluralize('user', usernames.length)) }}
        {{ usernames.join(', ') }} may still have some access to the items (
        <em>i.e.</em>
        via group permissions or public access)
      </p>
    </section>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import pluralize from 'pluralize'
import { capitalize } from '@/filters/filters'
import BatchEditPermissionsWidget from '@/components/listings/BatchEditPermissionsWidget.vue'
import ItemDetailField from '@/components/fields/ItemDetailField.vue'

const OPERATIONS = ['grant', 'revoke']

export default {
  name: 'BatchEditPermissionsForm',
  components: { BatchEditPermissionsWidget, ItemDetailField },
  props: {
    permissionsField: { type: Object, required: true },
  },
  emits: ['public-access-payload-unit-change', 'payload-unit-change'],
  setup(props, { emit }) {
    const showHelp = ref(true)
    const publicAccess = ref(null)
    const fineTuneAccess = ref(false)
    const chosenOperation = ref('grant')
    const permissions = ref([])
    const users = ref([])
    const groups = ref([])

    function toggleFineTune() {
      fineTuneAccess.value = !fineTuneAccess.value
      chosenOperation.value = 'grant'
      permissions.value = []
      users.value = []
      groups.value = []
    }

    function togglePublicAccess(setTo) {
      publicAccess.value = publicAccess.value === setTo ? null : setTo
    }

    const hasTunedAccess = computed(
      () =>
        fineTuneAccess.value &&
        chosenOperation.value &&
        permissions.value.length > 0 &&
        users.value.length + groups.value.length > 0,
    )

    const publicAccessPayloadUnit = computed(() => {
      if (publicAccess.value === null) return null
      return { op: 'replace', path: '/public', value: publicAccess.value }
    })

    const payloadUnit = computed(() => {
      if (!hasTunedAccess.value) return null
      const op = chosenOperation.value === 'grant' ? 'add' : 'remove'
      return {
        op,
        id: props.permissionsField?.name,
        path: `/${props.permissionsField.name}`,
        label: props.permissionsField.label,
        value: {
          permissions: permissions.value.map((p) => p.value),
          users: users.value,
          groups: groups.value,
        },
        // hasTunedAccess gate above already ensures permissions + users/groups
        // are present, so the payload is always valid when it exists.
        validated: true,
      }
    })

    const permissionsLabels = computed(() =>
      permissions.value.map((p) => p.label),
    )
    const usernames = computed(() => users.value.map((u) => u.username))
    const groupNames = computed(() => groups.value.map((g) => g.name))

    watch(payloadUnit, (p) => emit('payload-unit-change', p), {
      immediate: true,
    })
    watch(
      publicAccessPayloadUnit,
      (p) => emit('public-access-payload-unit-change', p),
      { immediate: true },
    )

    return {
      showHelp,
      publicAccess,
      fineTuneAccess,
      chosenOperation,
      permissions,
      users,
      groups,
      hasTunedAccess,
      permissionsLabels,
      usernames,
      groupNames,
      toggleFineTune,
      togglePublicAccess,
      capitalize,
      pluralize,
      OPERATIONS,
    }
  },
}
</script>
